import type { CredentialResponse } from "@react-oauth/google";

import * as React from "react";

import { prefilledErrorMapping } from "@components/account/common";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { storeToken } from "@editor/reducers/utils/store-token";
import {
  getReferrerKeyFromURL,
  isValidCookie,
  REFERRER_KEY_COOKIE,
} from "@editor/utils/referralCode";
import { trpc } from "@editor/utils/trpc";

import get from "lodash-es/get";
import some from "lodash-es/some";
import { useCookies } from "react-cookie";
import { useForm, useWatch } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { isEmpty } from "replo-utils/lib/misc";
import { z } from "zod";

const emailSchema = z.string().email();
const passwordSchema = z
  .string()
  .min(8, "Passwords must contain at least 8 characters.")
  .max(100, "Passwords must contain at most 100 characters.");

type SignupFormValues = {
  email: string;
  password: string;
};

const useSetReferrerKeyCookie = () => {
  const referrerKeyFromURL = getReferrerKeyFromURL();
  const [cookies, setCookie] = useCookies([REFERRER_KEY_COOKIE]);
  const cookieReferrerKey = cookies.replo_referrerKey;

  React.useEffect(() => {
    const isCookieSet = cookieReferrerKey;
    const isCookieStringAndNotEqualToReferrerKeyFromURL =
      typeof cookieReferrerKey === "string" &&
      cookieReferrerKey !== referrerKeyFromURL;
    const isCookieObjectAndNotEqualToReferrerKeyFromURL =
      typeof cookieReferrerKey === "object" &&
      cookieReferrerKey.referrerKey !== referrerKeyFromURL;

    if (
      referrerKeyFromURL &&
      (!isCookieSet ||
        isCookieStringAndNotEqualToReferrerKeyFromURL ||
        isCookieObjectAndNotEqualToReferrerKeyFromURL)
    ) {
      setCookie(REFERRER_KEY_COOKIE, {
        referrerKey: referrerKeyFromURL,
        createdAt: new Date(),
      });
    }
  }, [referrerKeyFromURL, setCookie, cookieReferrerKey]);
};

const useGetReferrerKeyCookiePayload = () => {
  const [cookies] = useCookies([REFERRER_KEY_COOKIE]);
  const cookieReferrerKey = cookies.replo_referrerKey;

  return React.useCallback(() => {
    if (!isValidCookie(cookieReferrerKey)) {
      return null;
    }
    if (typeof cookieReferrerKey === "string") {
      return cookieReferrerKey;
    }
    return cookieReferrerKey.referrerKey;
  }, [cookieReferrerKey]);
};

export const useSignupForm = () => {
  const [searchParams] = useSearchParams();
  const [signupErrorMessage, setSignupErrorMessage] = React.useState<
    string | null
  >(get(prefilledErrorMapping, searchParams.get("error") ?? ""));

  const getReferrerKeyFromCookie = useGetReferrerKeyCookiePayload();
  useSetReferrerKeyCookie();
  const analytics = useLogAnalytics();

  const { mutate: setUpInitialWorkspaceAndProject } =
    trpc.onboarding.setUpInitialWorkspaceAndProject.useMutation({
      onSuccess: (data) => {
        // Note (Ben O., if the user has been invited to a workspace, they will not have a new workspace created and will skip the setup step)
        if (data.newWorkspaceCreated) {
          const isUsingReaploOnboarding = isFeatureEnabled("reaplo");
          const firstStep = isUsingReaploOnboarding
            ? "onboarding-user-info"
            : "setup";
          window.location.replace(
            `/onboarding/${firstStep}${searchParams ? `?${searchParams}` : ""}`,
          );
        } else {
          window.location.replace(`/onboarding/workspace-details?invited=true`);
        }
      },
    });

  const handleAccountCreation = React.useCallback(
    (token: string, userEmail: string, referralCode: string | null) => {
      storeToken(token);
      if (referralCode) {
        analytics("referralCode.lead", {
          code: referralCode,
          referredUserEmail: userEmail,
        });
      }

      setUpInitialWorkspaceAndProject();
    },
    [analytics, setUpInitialWorkspaceAndProject],
  );

  const { mutate: createGoogleAccountApi, error: createGoogleAccountError } =
    trpc.auth.createAccountWithGoogle.useMutation({
      onSuccess: ({ token, user: { email } }, { referrerKey }) =>
        handleAccountCreation(token, email, referrerKey ?? null),
    });

  const {
    mutate: createAccountApi,
    isPending: isLoadingCreateAccount,
    error: createAccountError,
  } = trpc.auth.createAccount.useMutation({
    onSuccess: ({ token, user: { email } }, { referrerKey }) =>
      handleAccountCreation(token, email, referrerKey ?? null),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    control,
  } = useForm<SignupFormValues>({
    defaultValues: {
      email: searchParams.get("email") ?? "",
      password: "",
    },
    mode: "onBlur",
  });

  const watchAllFields = useWatch({ control });
  const someFieldIsEmpty = some(Object.values(watchAllFields), isEmpty);

  const onGoogleSubmit = React.useCallback(
    async (credentialResponse: CredentialResponse) => {
      const { clientId, credential } = credentialResponse;
      if (clientId && credential) {
        const referralCode = getReferrerKeyFromCookie();
        setSignupErrorMessage(null);
        createGoogleAccountApi({
          clientId,
          credential,
          installationNonce: searchParams.get("nonce"),
          referrerKey: referralCode,
        });
      }
    },
    [createGoogleAccountApi, getReferrerKeyFromCookie, searchParams],
  );

  const onSubmit = React.useCallback(
    async ({ email, password }: SignupFormValues) => {
      const referralCode = getReferrerKeyFromCookie();
      setSignupErrorMessage(null);
      createAccountApi({
        email,
        password,
        installationNonce: searchParams.get("nonce"),
        referrerKey: referralCode,
      });
    },
    [createAccountApi, getReferrerKeyFromCookie, searchParams],
  );

  const emailError = !isEmpty(watchAllFields.email)
    ? errors?.email?.message
    : undefined;

  const passwordError = !isEmpty(watchAllFields.password)
    ? errors?.password?.message
    : undefined;

  const buttonShouldBeDisabled =
    someFieldIsEmpty ||
    Boolean(passwordError) ||
    Boolean(emailError) ||
    isLoadingCreateAccount;

  const displayErrorMessage =
    signupErrorMessage ||
    createGoogleAccountError?.message ||
    createAccountError?.message ||
    null;

  const emailRegisterProps = register("email", {
    required: "Please enter a valid email address.",
    validate: (value) => {
      const parsedEmail = emailSchema.safeParse(value);
      if (parsedEmail.success) {
        return true;
      }
      return parsedEmail.error?.errors[0]?.message;
    },
    onChange: () => clearErrors("email"),
  });

  const passwordRegisterProps = register("password", {
    required: "Please enter a valid password.",
    validate: (value) => {
      const parsedPassword = passwordSchema.safeParse(value);
      if (parsedPassword.success) {
        return true;
      }
      return parsedPassword.error?.errors[0]?.message;
    },
    onChange: () => clearErrors("password"),
  });

  return {
    register,
    emailRegisterProps,
    passwordRegisterProps,
    handleSubmit,
    onSubmit,
    onGoogleSubmit,
    errors,
    emailError,
    passwordError,
    isLoadingCreateAccount,
    buttonShouldBeDisabled,
    displayErrorMessage,
    control,
  };
};
